import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, untracked } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TaskSortControlsComponent } from '@app/components/task/task-list-toolbar/task-sort-controls/task-sort-controls.component';
import { TaskSplitviewListItemComponent } from '@app/components/task/task-splitview-list/task-splitview-list-item/task-splitview-list-item.component';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';
import {
  MOBILE_TASK_GROUPING_OPTIONS,
  MobileTaskGrouping,
  TaskFiltersService
} from '../task-list-toolbar/task-filters.service';
import { getTaskListGroupingSignature, groupTaskListPage } from './task-list-grouping.util';

/**
 * @summary Card/list renderer for mobile and compact splitview task browsing.
 * @selector proffeo-task-cards-list
 */
@Component({
  selector: 'proffeo-task-cards-list',
  templateUrl: './task-cards-list.component.html',
  styleUrl: './task-cards-list.component.scss',
  imports: [
    NgTemplateOutlet,
    MatIconModule,
    MatTooltipModule,
    UiSelectComponent,
    TaskSplitviewListItemComponent,
    TaskSortControlsComponent,
    TranslatePipe,
    UiSpinnerDirective
  ],
  host: {
    class: 'flex w-full flex-col gap-2',
    '[class.task-cards-list--mobile-compact]': 'isMobileCompact()'
  }
})
export class TaskCardsListComponent {
  private readonly device = inject(DeviceService);
  private readonly filters = inject(TaskFiltersService);
  protected readonly isMobile = this.device.isMobile;
  protected readonly grouping = this.filters.mobileGrouping;
  protected readonly groups = computed(() => groupTaskListPage(this.tasks(), this.grouping()));
  protected readonly groupingStateKey = computed(
    () => `${this.grouping()}:${getTaskListGroupingSignature(this.groups())}`
  );
  protected readonly expandedGroups = this.filters.expandedTaskGroupIds;

  protected readonly groupViews = computed(() =>
    this.groups().map(group => ({ ...group, expanded: this.expandedGroups().has(group.id) }))
  );
  protected readonly allGroupsExpanded = computed(
    () => this.groups().length > 0 && this.groups().every(group => this.expandedGroups().has(group.id))
  );
  protected readonly groupToggleAction = computed(() =>
    this.allGroupsExpanded()
      ? { icon: 'unfold_less', label: 'task-list-grouping.collapse-all' }
      : { icon: 'unfold_more', label: 'task-list-grouping.expand-all' }
  );
  protected readonly mobileGrouping = this.filters.mobileGrouping;
  protected readonly groupingOptions = MOBILE_TASK_GROUPING_OPTIONS;
  protected readonly isMobileCompact = computed(() => this.device.isMobile() && this.mobileCompact());
  protected readonly canCollapseTree = computed(() => this.expandedNodes().size > 0);
  protected readonly showTreeControls = computed(() => this.isMobile());
  protected readonly isEmpty = computed(() => this.tasks().length === 0);

  /** @summary Root tasks to render as cards. */
  public readonly tasks = input.required<readonly Task[]>();
  /** @summary Active company task types for type selectors. */
  public readonly taskTypes = input.required<TaskType[]>();
  /** @summary Compact card mode used in the desktop split pane. */
  public readonly compactMode = input(false);
  /** @summary Mobile table-equivalent layout selected by the parent view. */
  public readonly mobileCompact = input(false);
  /** @summary Whether to show the compact sort controls above the cards. */
  public readonly showSortControls = input(false);
  /** @summary Covers only the task rows so grouping/sort chips stay visible while the page loads. */
  public readonly listLoading = input(false);
  /** @summary Shared set of expanded task ids for the recursive card tree. */
  public readonly expandedNodes = model.required<Set<string>>();

  public readonly statusChanged = output<{ status: TaskStatus; task: Task }>();
  public readonly priorityChanged = output<{ priority: TaskPriority; task: Task }>();
  public readonly taskStartDateSelected = output<{ date: Date; task: Task }>();
  public readonly taskEndDateSelected = output<{ date: Date; task: Task }>();
  public readonly taskProgressSelected = output<{ progress: number; task: Task }>();
  public readonly industryClicked = output<Task>();
  public readonly locationClicked = output<Task>();
  public readonly changeGroupClicked = output<Task>();
  public readonly changeUsersClicked = output<Task>();

  public constructor() {
    effect(() => {
      const signature = this.groupingStateKey();
      untracked(() => this.filters.reconcileTaskGroups(signature));
    });
  }
  protected collapseTree(): void {
    this.expandedNodes.set(new Set());
  }

  protected changeGrouping(value: MobileTaskGrouping | MobileTaskGrouping[] | null): void {
    if (value === null || Array.isArray(value)) {
      return;
    }

    if (value === 'none') {
      this.expandedGroups.set(new Set());
    }
    this.mobileGrouping.set(value);
  }

  protected toggleAllGroups(): void {
    this.expandedGroups.set(this.allGroupsExpanded() ? new Set() : new Set(this.groups().map(group => group.id)));
  }

  protected toggleGroup(id: string): void {
    this.expandedGroups.update(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
}
